/**
 * client-diagnostics.ts — Hộp đen nhỏ của trình duyệt: nhớ vài LỖI JavaScript và vài LỜI GỌI API thất bại gần nhất, để khi
 * người dùng bấm nút bong bóng "Góp ý" (feedback-bubble.tsx) thì đính kèm giúp đội phát triển tái hiện lỗi.
 *
 * Quyền riêng tư: chỉ nhớ thông điệp lỗi + đường dẫn (bỏ `?query`/`#hash`, bỏ tên miền) + mã trạng thái — KHÔNG bao giờ
 * nhớ nội dung request / response, tiêu đề (Authorization) hay token. Chỉ gửi đi khi người dùng tick đồng ý trong hộp góp ý.
 */
import axios from 'axios'
import { api, setRefreshFailureListener } from './api-client'

export interface ClientError { message: string; source: string; at: string }
export interface ApiFailure { method: string; url: string; status: number; at: string; /** Mã lý do của máy chủ (vd TOKEN_REUSE_DETECTED) — chỉ ghi cho lời gọi làm mới phiên. */ code?: string }

export const MAX_KEPT = 10

/** Thêm vào cuối, giữ tối đa `max` mục mới nhất. Không sửa mảng gốc. */
export function pushRing<T>(list: readonly T[], item: T, max = MAX_KEPT): T[] {
  const next = [...list, item]
  return next.length > max ? next.slice(next.length - max) : next
}

/** Chỉ giữ đường dẫn: bỏ tên miền, `?query`, `#hash` (có thể chứa token / mã / số điện thoại). */
export function pathOnly(url: string | undefined | null, max = 200): string {
  return (url ?? '').replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0].slice(0, max)
}

let errors: ClientError[] = []
let failures: ApiFailure[] = []
let installed = false

export function recordClientError(message: string, source = ''): void {
  const m = String(message || '').slice(0, 500)
  if (!m) return
  errors = pushRing(errors, { message: m, source: pathOnly(source), at: new Date().toISOString() })
}

/** Mã lý do hợp lệ: chữ in hoa / số / gạch dưới, ≤ 40 ký tự (không nhận chuỗi tuỳ ý từ máy chủ). */
export const cleanReasonCode = (code: unknown): string | undefined =>
  typeof code === 'string' && /^[A-Z0-9_]{1,40}$/.test(code) ? code : undefined

export function recordApiFailure(method: string | undefined, url: string | undefined, status: number, code?: string): void {
  const u = pathOnly(url)
  if (!u) return
  const c = cleanReasonCode(code)
  failures = pushRing(failures, { method: (method || 'get').toUpperCase(), url: u, status, at: new Date().toISOString(), ...(c ? { code: c } : {}) })
}

export function getDiagnostics(): { errors: ClientError[]; apiFailures: ApiFailure[] } {
  return { errors: [...errors], apiFailures: [...failures] }
}

/** Dùng cho test. */
export function resetDiagnostics(): void {
  errors = []
  failures = []
}

/** Gắn một lần khi khởi động app (main.tsx): lỗi JS chưa bắt + promise bị từ chối + API lỗi. */
export function installClientDiagnostics(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  window.addEventListener('error', (e) => {
    recordClientError(e.message || (e.error as Error | undefined)?.message || 'Lỗi không rõ', e.filename)
  })
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason as { message?: string } | string | undefined
    recordClientError(typeof r === 'string' ? r : r?.message || 'Promise bị từ chối', '')
  })
  // Làm mới phiên bị từ chối (401 = phiên bị thu hồi / token bị xoay lặp…) → ghi để góp ý "bị đăng xuất" có mã lý do.
  setRefreshFailureListener((status, code) => recordApiFailure('POST', '/auth/refresh', status, code))
  api.interceptors.response.use(undefined, (err: unknown) => {
    if (!axios.isCancel(err)) {
      const e = err as { config?: { method?: string; url?: string }; response?: { status?: number } }
      const url = e.config?.url
      // Chính việc gửi góp ý không tự ghi vào hộp đen; 401 là hết phiên (đã có hộp đăng nhập lại), không phải lỗi.
      if (url && !url.startsWith('/feedback') && e.response?.status !== 401) {
        recordApiFailure(e.config?.method, url, e.response?.status ?? 0)
      }
    }
    return Promise.reject(err)
  })
}
